import { BigNumber } from '@ethersproject/bignumber';
import { useLTV } from 'app/features/hooks/useLTV';
import { LTV } from 'app/features/util/ltv';
import { sumNullableNumbers } from 'app/functions/silo/calculations';
import { SiloAsset } from 'app/queries/decoratedSilo';
import { RefObject, useMemo, useRef, useState } from 'react';
import { useNormalizedTokenInputAmount } from 'app/hooks/useNormalizedTokenInputAmount';
import { useAmountInUSD } from '../../common/ActionForm/useAmountInUSD';
import { decideIsFullAmount } from '../FullAmountWarning';
import { getRemainingAmount } from './getRemainingAmount';
import { MaxProps, useMaxValues } from './useMaxValues';
import { DoWithdrawals, SubmissionProps, useSubmission } from './useSubmission';
import { useValidation, ValidationProps } from './useValidation';

type UseDualCollateral = (params: { selectedAsset: SiloAsset; doWithdrawals: DoWithdrawals }) => {
  lendingAmount: string;
  setLendingAmount: (amount: string) => void;
  lendingAmountRef: RefObject<HTMLInputElement>;
  protectedAmount: string;
  setProtectedAmount: (amount: string) => void;
  normalizedLendingAmount: BigNumber | null;
  normalizedProtectedAmount: BigNumber | null;
  normalizedTotalAmount: BigNumber | null;
  isFullLendingAmount: boolean;
  isFullProtectedAmount: boolean;
  currentLTV: LTV | null;
  remainingAmount: BigNumber | null;
} & MaxProps &
  ValidationProps &
  SubmissionProps;

export const useDualCollateral: UseDualCollateral = ({ selectedAsset, doWithdrawals }) => {
  const [lendingAmount, setLendingAmount] = useState('');
  const resetLendingAmount = (): void => {
    setLendingAmount('');
  };
  const [protectedAmount, setProtectedAmount] = useState('');
  const resetProtectedAmount = (): void => {
    setProtectedAmount('');
  };

  const lendingAmountRef = useRef<HTMLInputElement>(null);

  const normalizedLendingAmount = useNormalizedTokenInputAmount(lendingAmount, selectedAsset.decimals);
  const normalizedProtectedAmount = useNormalizedTokenInputAmount(protectedAmount, selectedAsset.decimals);
  const normalizedTotalAmount = useMemo(
    () => sumNullableNumbers([normalizedLendingAmount, normalizedProtectedAmount]),
    [normalizedLendingAmount, normalizedProtectedAmount]
  );

  const currentLTV = useLTV();

  const { maxLendingAmount, maxProtectedAmount, doLendingMax, doProtectedMax } = useMaxValues({
    currentLTV,
    selectedAsset,
    setLendingAmount,
    setProtectedAmount,
  });

  const isFullLendingAmount = useIsFullAmount({
    selectedAsset,
    normalizedAmount: normalizedLendingAmount,
    balance: selectedAsset.collateralBalance,
  });

  const isFullProtectedAmount = useIsFullAmount({
    selectedAsset,
    normalizedAmount: normalizedProtectedAmount,
    balance: selectedAsset.collateralOnlyBalance,
  });

  const { formError, ...validationProps } = useValidation({
    selectedAsset,
    normalizedLendingAmount,
    normalizedProtectedAmount,
    maxLendingAmount,
    maxProtectedAmount,
    currentLTV,
    isFullLendingAmount,
    isFullProtectedAmount,
  });

  const { submissionFormError, ...submissionProps } = useSubmission({
    selectedAsset,
    doWithdrawals,
    normalizedLendingAmount,
    normalizedProtectedAmount,
    resetLendingAmount,
    resetProtectedAmount,
    lendingAmountRef,
    isFullLendingAmount,
    isFullProtectedAmount,
  });

  const remainingAmount = getRemainingAmount({
    normalizedLendingAmount,
    normalizedProtectedAmount,
    isFullLendingAmount,
    isFullProtectedAmount,
    selectedAsset,
  });

  return {
    ...validationProps,
    ...submissionProps,
    lendingAmount,
    setLendingAmount,
    lendingAmountRef,
    protectedAmount,
    setProtectedAmount,
    normalizedLendingAmount,
    normalizedProtectedAmount,
    normalizedTotalAmount,
    maxLendingAmount,
    maxProtectedAmount,
    doLendingMax,
    doProtectedMax,
    isFullLendingAmount,
    isFullProtectedAmount,
    currentLTV,
    remainingAmount,
    formError: formError || submissionFormError,
  };
};

type UseIsFullAmount = (params: {
  selectedAsset: SiloAsset;
  normalizedAmount: BigNumber | null;
  balance: BigNumber;
}) => boolean;

const useIsFullAmount: UseIsFullAmount = ({ selectedAsset, normalizedAmount, balance }) => {
  const { amountInUSD, priceInUSD } = useAmountInUSD({ selectedAsset, normalizedAmount });

  return useMemo(
    () => decideIsFullAmount({ balance, amountInUSD, priceInUSD, selectedAsset, normalizedTokenAmount: normalizedAmount }),
    [balance, amountInUSD, priceInUSD, selectedAsset, normalizedAmount]
  );
};
